import { Alert, Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography, useTheme } from "@mui/material";
import { Bar, BarChart, CartesianGrid, Cell, ComposedChart, Line, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from "recharts";

/* ───────────── MineProd status rule: good = at/better than plan, warning = <3% worse, critical = >=3% worse ───────────── */
export const THRESH = 3;
export const STATUS = {
  good: { icon: "✓", label: "On plan or better", color: "#0CA30C", ink: "#fff" },
  warning: { icon: "!", label: "Within 3% of plan", color: "#FAB219", ink: "#3A2A00" },
  critical: { icon: "✕", label: "3% or more off plan", color: "#D03B3B", ink: "#fff" },
  none: { icon: "–", label: "No plan target", color: "#9CA3AF", ink: "#fff" },
};
export const varPct = (a, p) => (p ? ((a - p) / Math.abs(p)) * 100 : null);
export const statusOf = (a, p, better = "higher") => {
  if (a == null || p == null || !Number.isFinite(a) || !Number.isFinite(p) || p === 0) return "none";
  const v = varPct(a, p);
  const w = better === "lower" ? -v : v;
  return w >= -1e-9 ? "good" : w > -THRESH ? "warning" : "critical";
};
export const statusPhrase = (st, basis = "plan") => {
  const t = basis === "plan" ? "plan" : "the previous period";
  return { good: `On or better than ${t}`, warning: `Slightly behind ${t} (under 3%)`, critical: `Behind ${t} by 3% or more`, none: basis === "plan" ? "No plan target" : "No comparison available" }[st];
};
export const deltaColor = (st) => ({ good: "success.main", warning: "warning.dark", critical: "error.main" }[st] || "text.secondary");

/* ───────────── formatting ───────────── */
export const fmt = (v, d = 0) =>
  v == null || !Number.isFinite(v) ? "—" : (v < 0 ? "−" : "") + Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
export const fmtT = (v) => {
  if (v == null || !Number.isFinite(v)) return "—";
  const a = Math.abs(v), s = v < 0 ? "−" : "";
  if (a >= 1e6) return `${s}${(a / 1e6).toFixed(2)} Mt`;
  if (a >= 1e4) return `${s}${(a / 1e3).toFixed(1)} kt`;
  return `${s}${Math.round(a).toLocaleString()} t`;
};
export const fmtPct = (v, d = 1, signed = false) =>
  v == null || !Number.isFinite(v) ? "—" : `${v > 0 && signed ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}%`;
export const signed = (v, d = 0, unit = "") => (v == null || !Number.isFinite(v) ? "—" : `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}${unit}`);
export const shortNum = (v) => {
  const a = Math.abs(v);
  return a >= 1e6 ? `${+(v / 1e6).toFixed(2)}M` : a >= 1e3 ? `${+(v / 1e3).toFixed(1)}k` : `${+(+v).toFixed(a < 10 ? 2 : 0)}`;
};
export const hexA = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${a})`;
};

/* ───────────── dates / presets ───────────── */
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const addDays = (iso, n) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};
export const todayISO = () => new Date().toISOString().slice(0, 10);
export const PRESETS = [["l7", "Last 7 days"], ["l14", "Last 14 days"], ["l30", "Last 30 days"], ["mtd", "Month to date"], ["lm", "Last full month"], ["qtd", "Quarter to date"], ["ytd", "Year to date"], ["all", "All data (max 366 days)"], ["custom", "Custom range"]];
/** Presets are relative to the last day that has data, so historical datasets still show something. */
export function presetRange(key, dataRange) {
  const asOf = dataRange?.endDate || todayISO();
  const first = dataRange?.startDate || addDays(asOf, -365);
  const [y, m] = asOf.split("-").map(Number);
  const iso = (Y, M, D) => new Date(Date.UTC(Y, M, D)).toISOString().slice(0, 10);
  switch (key) {
    case "l7": return { start: addDays(asOf, -6), end: asOf };
    case "l14": return { start: addDays(asOf, -13), end: asOf };
    case "mtd": return { start: iso(y, m - 1, 1), end: asOf };
    case "lm": return { start: iso(y, m - 2, 1), end: iso(y, m - 1, 0) };
    case "qtd": return { start: iso(y, Math.floor((m - 1) / 3) * 3, 1), end: asOf };
    case "ytd": return { start: iso(y, 0, 1), end: asOf };
    case "all": { const cap = addDays(asOf, -365); return { start: first < cap ? cap : first, end: asOf }; }
    default: return { start: addDays(asOf, -29), end: asOf };
  }
}
export const bucketLabel = (iso, grain) => {
  const [y, m, d] = iso.split("-").map(Number);
  if (grain === "month") return `${MON[m - 1]} ${y}`;
  if (grain === "week") return `Wk ${d} ${MON[m - 1]}`;
  return `${d} ${MON[m - 1]}`;
};
export const bucketTitle = (iso, grain) => {
  const [y, m, d] = iso.split("-").map(Number);
  if (grain === "month") return `${MON[m - 1]} ${y}`;
  if (grain === "week") return `Week of ${d} ${MON[m - 1]} ${y}`;
  return `${d} ${MON[m - 1]} ${y}`;
};
export const cumsum = (a) => { let s = 0; return a.map((v) => (v == null ? null : (s += v))); };

/* ───────────── equipment time model (business definition, semantic layer G16) ─────────────
   PA = Available/Calendar · UA = GOH/Available · OE = NOH/GOH · Standby = Available−GOH · Breakdown = Calendar−Available */
export const timeMetrics = (a, plan = false) => {
  const cal = plan ? a.pCal : a.cal, avail = plan ? a.pAvail : a.avail, goh = plan ? a.pGoh : a.goh, noh = plan ? a.pNoh : a.noh, delay = plan ? a.pDelay : a.delay;
  return { cal, avail, goh, noh, delay, standby: Math.max(0, avail - goh), bd: Math.max(0, cal - avail),
    pa: cal ? (avail / cal) * 100 : null, ua: avail ? (goh / avail) * 100 : null, oe: goh ? (noh / goh) * 100 : null };
};
export const sumAgg = (list) => list.reduce((s, a) => { Object.keys(a).forEach((k) => { s[k] = (s[k] || 0) + a[k]; }); return s; }, {});

/* ───────────── theme-aware chart colours ───────────── */
export function useChartColors() {
  const t = useTheme();
  const dark = t.palette.mode === "dark";
  return {
    grid: t.palette.divider, plan: dark ? "#9AA39C" : "#7D8780", hover: t.palette.action.hover, paper: t.palette.background.paper,
    text: t.palette.text.primary, muted: t.palette.text.secondary, brand: dark ? "#3FA37A" : "#0B5D3B", gold: "#C9A227",
    tick: { fontSize: 11, fill: t.palette.text.secondary },
    series: ["#2A78D6", "#EB6834", "#1BAF7A", "#EDA100", "#E87BA4", "#6250D6", "#E34948", "#008300"],
  };
}

/* ───────────── small UI pieces ───────────── */
export function StatusDot({ status, size = 16, title }) {
  const s = STATUS[status] || STATUS.none;
  return (
    <Box component="span" title={title || s.label} aria-label={title || s.label}
      sx={{ display: "inline-grid", placeItems: "center", width: size, height: size, borderRadius: "50%", bgcolor: s.color, color: s.ink, fontSize: size * 0.6, fontWeight: 700, lineHeight: 1, flexShrink: 0 }}>
      {s.icon}
    </Box>
  );
}

export function StatusKey({ basis = "plan" }) {
  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
      {["good", "warning", "critical"].map((s) => (
        <Box key={s} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><StatusDot status={s} size={13} /><Typography variant="caption" color="text.secondary">{statusPhrase(s, basis)}</Typography></Box>
      ))}
    </Box>
  );
}

export function KpiBand({ items }) {
  return (
    <Paper variant="outlined" sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "1px", bgcolor: "divider", overflow: "hidden" }}>
      {items.map((k) => (
        <Box key={k.label} onClick={k.onClick} sx={{ bgcolor: "background.paper", p: 1.5, minWidth: 0, cursor: k.onClick ? "pointer" : "default", "&:hover": k.onClick ? { bgcolor: "action.hover" } : undefined }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography noWrap color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600, fontSize: "0.65rem" }}>{k.label}</Typography>
            {k.status && k.status !== "none" && <StatusDot status={k.status} size={14} />}
          </Box>
          <Typography variant="h6" fontWeight={700} noWrap sx={{ lineHeight: 1.25 }}>{k.value}</Typography>
          <Typography variant="caption" color="text.secondary" component="div" noWrap>
            {k.sub}
            {k.delta != null && Number.isFinite(k.delta) && <Box component="span" sx={{ ml: 0.75, fontWeight: 600, color: deltaColor(k.status) }}>{fmtPct(k.delta, 1, true)}</Box>}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
}

export function ChartCard({ title, sub, action, children, sx }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0, ...sx }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mb: 1, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
          {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </Box>
        {action}
      </Box>
      {children}
    </Paper>
  );
}

export function Seg({ value, onChange, options }) {
  return (
    <ToggleButtonGroup size="small" exclusive value={value} onChange={(_, v) => v != null && onChange(v)} sx={{ "& .MuiToggleButton-root": { py: 0.25, px: 1, fontSize: 12, textTransform: "none" } }}>
      {options.map(([v, l]) => <ToggleButton key={String(v)} value={v}>{l}</ToggleButton>)}
    </ToggleButtonGroup>
  );
}

export function Empty({ children = "No data for the current filters" }) {
  return <Box sx={{ py: 5, textAlign: "center", color: "text.secondary", fontSize: 13 }}>{children}</Box>;
}

/** cols: [{ key, label, align, render(row) }] */
export function SimpleTable({ cols, rows, max = 420, onRow }) {
  return (
    <Box sx={{ overflow: "auto", maxHeight: max, border: 1, borderColor: "divider", borderRadius: 1 }}>
      <Table size="small" stickyHeader>
        <TableHead><TableRow>{cols.map((c) => <TableCell key={c.key} align={c.align || "right"} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>{c.label}</TableCell>)}</TableRow></TableHead>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={cols.length}><Empty /></TableCell></TableRow>}
          {rows.map((r, i) => (
            <TableRow key={i} hover onClick={onRow ? () => onRow(r) : undefined} sx={{ cursor: onRow ? "pointer" : "default" }}>
              {cols.map((c) => <TableCell key={c.key} align={c.align || "right"} sx={{ whiteSpace: "nowrap" }}>{c.render ? c.render(r) : r[c.key]}</TableCell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

export function SectionFrame({ eyebrow, title, desc, state, children }) {
  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="overline" sx={{ color: "warning.main", fontWeight: 700, letterSpacing: 1 }}>{eyebrow}</Typography>
        <Typography variant="h5" fontWeight={700}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{desc}</Typography>
      </Box>
      {state.error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={state.reload}>Retry</Button>}>{state.error}</Alert>}
      {state.loading && !state.data && <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>}
      {state.data && (
        <Box sx={{ opacity: state.loading ? 0.55 : 1, transition: "opacity .2s", display: "flex", flexDirection: "column", gap: 2 }}>{children}</Box>
      )}
    </Box>
  );
}

/* ───────────── plan-vs-actual charts ───────────── */
export function PATooltip({ active, payload, label, valueFmt = fmt, better = "higher", actualKey = "actual", planKey = "plan" }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const a = row[actualKey], p = row[planKey], st = statusOf(a, p, better);
  return (
    <Paper elevation={4} sx={{ p: 1.25, minWidth: 170 }}>
      <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>{row.title || label}</Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, fontSize: 12 }}><span>Actual</span><b>{valueFmt(a)}</b></Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, fontSize: 12 }}><span>Plan</span><b>{valueFmt(p)}</b></Box>
      {p != null && p !== 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5, fontSize: 12 }}><StatusDot status={st} size={14} />{fmtPct(varPct(a, p), 1, true)} vs plan</Box>
      )}
    </Paper>
  );
}

/** rows: [{ label, title, actual, plan }]. Bars (or line) coloured by MineProd status, dashed plan line. */
export function PlanActualChart({ data, better = "higher", valueFmt = fmt, yFmt = shortNum, line = false, height = 280, domain }) {
  const c = useChartColors();
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={c.grid} />
        <XAxis dataKey="label" tick={c.tick} minTickGap={14} />
        <YAxis tick={c.tick} width={58} tickFormatter={yFmt} domain={domain} />
        <RTooltip content={<PATooltip valueFmt={valueFmt} better={better} />} cursor={{ fill: c.hover }} />
        {line ? (
          <Line type="monotone" dataKey="actual" name="Actual" stroke={c.brand} strokeWidth={2.5} dot={{ r: data.length <= 40 ? 3 : 0 }} connectNulls />
        ) : (
          <Bar dataKey="actual" name="Actual" maxBarThickness={46} radius={[4, 4, 0, 0]} isAnimationActive={false}>
            {data.map((d, i) => <Cell key={i} fill={STATUS[statusOf(d.actual, d.plan, better)].color} fillOpacity={0.85} />)}
          </Bar>
        )}
        <Line type="monotone" dataKey="plan" name="Plan" stroke={c.plan} strokeDasharray="6 4" strokeWidth={2} dot={false} connectNulls />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/** Horizontal plan-vs-actual bars. rows: [{ name, plan, actual }] */
export function PlanActualBars({ data, better = "higher", valueFmt = fmtT, height, nameWidth = 110 }) {
  const c = useChartColors();
  const h = height || Math.max(170, data.length * 46 + 40);
  return (
    <ResponsiveContainer width="100%" height={h}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={c.grid} />
        <XAxis type="number" tick={c.tick} tickFormatter={shortNum} />
        <YAxis type="category" dataKey="name" width={nameWidth} tick={c.tick} />
        <RTooltip content={<PATooltip valueFmt={valueFmt} better={better} />} cursor={{ fill: c.hover }} />
        <Bar dataKey="plan" name="Plan" fill={c.plan} fillOpacity={0.45} barSize={12} radius={[0, 4, 4, 0]} isAnimationActive={false} />
        <Bar dataKey="actual" name="Actual" barSize={12} radius={[0, 4, 4, 0]} isAnimationActive={false}>
          {data.map((d, i) => <Cell key={i} fill={STATUS[statusOf(d.actual, d.plan, better)].color} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}